import { getStoredLoadout, isBothHanded, type CharacterEquipmentType, type HandsItem } from '@sorrem/shared';
import type { UIItemContainerKind } from '@sorrem-client/types';
import type { ContextMenuItem } from '@sorrem-client/ui/components';
import { LOADOUT_SLOTS } from './loadout-slots';
import { canEquipTypeAtSlotIndex, canMoveItem, findMoveRefusal, toEquipmentSlots, type ItemMoveContext } from './equipment-slots';

/** What an item menu entry does. */
export type ItemMenuAction = 'equip' | 'equip-off-hand' | 'unequip' | 'to-storage' | 'to-carried' | 'trade' | 'use' | 'drop' | 'details';

type MenuSelection = { readonly itemId: string; readonly container: UIItemContainerKind };
type MenuTarget = { readonly container: UIItemContainerKind; readonly index: number };

/**
 * One entry of an item's right-click menu (#267): the move it makes, if it
 * is a move, or why it can't be chosen now (shown grayed out, with the
 * reason).
 */
export interface ItemMenuEntry {
    readonly action: ItemMenuAction;
    readonly label: string;
    readonly target?: MenuTarget;
    readonly disabledReason?: string;
}

/** What the page the menu opens on can do beyond moving items between carried slots and the loadout. */
export interface ItemMenuOptions {
    /** Storage is in reach (Character Home). */
    readonly hasStorage: boolean;
    /** Items can be traded to a house of exchange (Character Home). */
    readonly canTrade: boolean;
    /** Crystals can be used (a run). */
    readonly canUse: boolean;
    /** Carried items can be dropped on the ground (a run). */
    readonly canDrop: boolean;
}

/** A menu item, as the move rules see it: equipment has a type, a crystal stack doesn't. */
type MenuItem = HandsItem & { readonly id: string; readonly equipmentType?: CharacterEquipmentType };

const NO_FREE_CARRIED = 'No free carried slot.';
const NO_FREE_STORAGE = 'Storage is full.';

/** A container's items; an empty slot is null in the loadout, undefined elsewhere. */
function itemsOf(context: ItemMoveContext, container: UIItemContainerKind): ReadonlyArray<MenuItem | null | undefined> {
    if (container === 'equipment') {
        return toEquipmentSlots(getStoredLoadout(context));
    }
    return container === 'storage' ? context.storedItems ?? [] : context.carriedItems;
}

/** The first empty slot of a carried or stored grid the item may move to, or null. */
function firstFreeSlot(context: ItemMoveContext, selection: MenuSelection, container: 'inventory' | 'storage'): MenuTarget | null {
    const length = container === 'inventory' ? context.carriedCapacity : context.storedItems?.length ?? 0;
    const items = itemsOf(context, container);
    for (let index = 0; index < length; index += 1) {
        const target = { container, index };
        if (!items[index] && canMoveItem(context, selection, target)) {
            return target;
        }
    }
    return null;
}

/**
 * Where Equip puts an item: the first free loadout slot of its kind the
 * rules allow, else the first one it may swap into. Null, with the rules'
 * reason for the first slot of its kind, when none will take it.
 */
function resolveEquipTarget(context: ItemMoveContext, selection: MenuSelection, equipmentType: CharacterEquipmentType): { target: MenuTarget } | { reason: string } {
    const loadout = toEquipmentSlots(getStoredLoadout(context));
    const slots = LOADOUT_SLOTS.flatMap((_, index) => (canEquipTypeAtSlotIndex(equipmentType, index) ? [index] : []));
    const allowed = slots.filter((index) => canMoveItem(context, selection, { container: 'equipment', index }));
    const index = allowed.find((candidate) => !loadout[candidate]) ?? allowed[0];
    if (index !== undefined) {
        return { target: { container: 'equipment', index } };
    }
    const first = slots[0];
    return { reason: first === undefined ? 'It can\'t be worn.' : findMoveRefusal(context, selection, { container: 'equipment', index: first }) ?? 'It can\'t be worn.' };
}

/** Main and off-hand slots in the loadout's flat order. */
const MAIN_HAND_INDEX = 0;
const OFF_HAND_INDEX = 1;

/** Equipping into one hand: the slot, or the rules' reason it won't take the item. */
function equipIntoHand(context: ItemMoveContext, selection: MenuSelection, action: ItemMenuAction, label: string, index: number): ItemMenuEntry {
    const target = { container: 'equipment' as const, index };
    const reason = findMoveRefusal(context, selection, target);
    return reason === undefined ? { action, label, target } : { action, label, disabledReason: reason };
}

/** A move entry: its target, or why it can't be made. */
function moveEntry(action: ItemMenuAction, label: string, target: MenuTarget | null, reason: string): ItemMenuEntry {
    return target === null ? { action, label, disabledReason: reason } : { action, label, target };
}

/**
 * The entries of an item's right-click menu, in order, judged by the same
 * rules as dragging it (`canMoveItem`): Equip (and Equip Off-Hand for a
 * one-handed weapon) or Unequip, Move to Storage
 * or Carried, Trade, Use, Drop, then Details. What the page can't do is
 * left out; what the item can't do now is grayed out with the reason.
 */
export function resolveItemMenuEntries(context: ItemMoveContext, selection: MenuSelection, options: ItemMenuOptions): ItemMenuEntry[] {
    const item = itemsOf(context, selection.container).find((entry) => entry?.id === selection.itemId);
    if (!item) {
        return [];
    }
    const entries: ItemMenuEntry[] = [];
    const isEquipped = selection.container === 'equipment';

    if (isEquipped) {
        const carried = firstFreeSlot(context, selection, 'inventory');
        const stored = carried === null && options.hasStorage ? firstFreeSlot(context, selection, 'storage') : null;
        entries.push(moveEntry('unequip', 'Unequip', carried ?? stored, NO_FREE_CARRIED));
    } else if (item.equipmentType !== undefined
        && canEquipTypeAtSlotIndex(item.equipmentType, MAIN_HAND_INDEX)
        && canEquipTypeAtSlotIndex(item.equipmentType, OFF_HAND_INDEX)
        && !isBothHanded(item)) {
        // A one-handed weapon: either hand, each its own entry. A shield takes the off hand only.
        entries.push(equipIntoHand(context, selection, 'equip', 'Equip', MAIN_HAND_INDEX));
        entries.push(equipIntoHand(context, selection, 'equip-off-hand', 'Equip Off-Hand', OFF_HAND_INDEX));
    } else if (item.equipmentType !== undefined) {
        const equip = resolveEquipTarget(context, selection, item.equipmentType);
        entries.push('target' in equip
            ? { action: 'equip', label: 'Equip', target: equip.target }
            : { action: 'equip', label: 'Equip', disabledReason: equip.reason });
    }

    if (options.hasStorage && selection.container === 'inventory') {
        entries.push(moveEntry('to-storage', 'Move to Storage', firstFreeSlot(context, selection, 'storage'), NO_FREE_STORAGE));
    }
    if (options.hasStorage && selection.container === 'storage') {
        entries.push(moveEntry('to-carried', 'Move to Carried', firstFreeSlot(context, selection, 'inventory'), NO_FREE_CARRIED));
    }

    // Only equipment trades: crystals are bound to a run.
    if (options.canTrade && item.equipmentType !== undefined) {
        entries.push(isEquipped ? { action: 'trade', label: 'Trade', disabledReason: 'Take it off to trade it.' } : { action: 'trade', label: 'Trade' });
    }
    if (options.canUse && item.equipmentType === undefined && selection.container === 'inventory') {
        entries.push({ action: 'use', label: 'Use' });
    }
    if (options.canDrop && selection.container === 'inventory') {
        entries.push({ action: 'drop', label: 'Drop' });
    }

    entries.push({ action: 'details', label: 'Details' });
    return entries;
}

/** What the page does for each entry; an entry with no handler here is left out. */
export interface ItemMenuHandlers {
    readonly move: (target: MenuTarget) => void;
    readonly details: () => void;
    readonly trade?: () => void;
    readonly use?: () => void;
    readonly drop?: () => void;
}

/** The menu's entries as `ContextMenu` items, each doing its part through the page's handlers. */
export function toItemMenuItems(entries: readonly ItemMenuEntry[], handlers: ItemMenuHandlers): ContextMenuItem[] {
    return entries.flatMap((entry): ContextMenuItem[] => {
        const onSelect = ((): (() => void) | undefined => {
            switch (entry.action) {
                case 'equip':
                case 'equip-off-hand':
                case 'unequip':
                case 'to-storage':
                case 'to-carried':
                    return entry.target === undefined ? (): void => undefined : (): void => handlers.move(entry.target as MenuTarget);
                case 'trade': return handlers.trade;
                case 'use': return handlers.use;
                case 'drop': return handlers.drop;
                case 'details': return handlers.details;
            }
        })();
        if (onSelect === undefined) {
            return [];
        }
        return [{ label: entry.label, onSelect, ...(entry.disabledReason !== undefined ? { disabledReason: entry.disabledReason } : {}) }];
    });
}
